import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useToast } from '@/context/ToastContext';
import { useAuth } from '@/context/AuthContext';
import { getPanier, ajouterAuPanier, modifierQuantitePanier, supprimerProduitPanier, viderPanier } from '@/api';
import { toNumber } from '@/utils/number';

export interface CartItem {
  productId: string;
  slug: string | null;
  ligneId: number;
  name: string;
  price: number;
  currency: string;
  image: string;
  quantity: number;
  boutiqueId?: string;
  boutiqueName?: string;
}

interface RawCommandeProduit {
  id: number;
  commande_id: number;
  product_id: number;
  slug: string | null;
  boutique_id: number | null;
  nom_produit: string;
  image_produit: string;
  prix_unitaire: number;
  quantite: number;
  boutique?: { id: number; nom: string } | null;
}

interface RawPanier {
  id: number;
  statut: string;
  sous_total: number;
  total: number;
  devise: string;
  produits: RawCommandeProduit[];
}

interface CartContextValue {
  items: CartItem[];
  isLoading: boolean;
  addToCart: (item: Omit<CartItem, 'quantity' | 'ligneId'>, quantity?: number) => Promise<void>;
  removeFromCart: (productId: string) => Promise<void>;
  updateQuantity: (productId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
  totalItems: number;
  totalPrice: number;
  isInCart: (productId: string) => boolean;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

function mapPanier(raw: RawPanier): CartItem[] {
  const devise = raw.devise || 'GNF';
  return (raw.produits || []).map((l) => ({
    productId: String(l.product_id),
    slug: l.slug,
    ligneId: l.id,
    name: l.nom_produit,
    price: toNumber(l.prix_unitaire),
    currency: devise,
    image: l.image_produit,
    quantity: l.quantite,
    boutiqueId: l.boutique_id != null ? String(l.boutique_id) : undefined,
    boutiqueName: l.boutique?.nom,
  }));
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();
  const { canShop } = useAuth();

  const fetchPanier = useCallback(async () => {
    if (!canShop) {
      setItems([]);
      return;
    }
    setIsLoading(true);
    try {
      const data = await getPanier();
      setItems(mapPanier(data.panier));
    } catch {
      // silencieux : le panier reste vide si le chargement initial echoue
    } finally {
      setIsLoading(false);
    }
  }, [canShop]);

  useEffect(() => {
    fetchPanier();
  }, [fetchPanier]);

  const addToCart = useCallback(
    async (item: Omit<CartItem, 'quantity' | 'ligneId'>, quantity: number = 1) => {
      try {
        const data = await ajouterAuPanier({ product_id: Number(item.productId), quantite: quantity });
        setItems(mapPanier(data.panier));
        addToast({ type: 'success', message: `"${item.name}" ajoute au panier !` });
      } catch (err: any) {
        addToast({ type: 'error', message: err.response?.data?.message || "Impossible d'ajouter ce produit au panier." });
      }
    },
    [addToast],
  );

  const removeFromCart = useCallback(
    async (productId: string) => {
      const line = items.find((i) => i.productId === productId);
      if (!line) return;
      try {
        const data = await supprimerProduitPanier(line.ligneId);
        setItems(mapPanier(data.panier));
        addToast({ type: 'info', message: `"${line.name}" retire du panier` });
      } catch (err: any) {
        addToast({ type: 'error', message: err.response?.data?.message || 'Impossible de retirer ce produit.' });
      }
    },
    [items, addToast],
  );

  const updateQuantity = useCallback(
    async (productId: string, quantity: number) => {
      const line = items.find((i) => i.productId === productId);
      if (!line) return;

      if (quantity <= 0) {
        try {
          const data = await supprimerProduitPanier(line.ligneId);
          setItems(mapPanier(data.panier));
          addToast({ type: 'info', message: `"${line.name}" retire du panier` });
        } catch (err: any) {
          addToast({ type: 'error', message: err.response?.data?.message || 'Impossible de retirer ce produit.' });
        }
        return;
      }

      try {
        const data = await modifierQuantitePanier(line.ligneId, quantity);
        setItems(mapPanier(data.panier));
      } catch (err: any) {
        addToast({ type: 'error', message: err.response?.data?.message || 'Impossible de mettre a jour la quantite.' });
      }
    },
    [items, addToast],
  );

  const clearCart = useCallback(async () => {
    try {
      const data = await viderPanier();
      setItems(mapPanier(data.panier));
      addToast({ type: 'warning', message: 'Panier vide' });
    } catch (err: any) {
      addToast({ type: 'error', message: err.response?.data?.message || 'Impossible de vider le panier.' });
    }
  }, [addToast]);

  const isInCart = useCallback(
    (productId: string) => items.some((i) => i.productId === productId),
    [items],
  );

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        isLoading,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        refreshCart: fetchPanier,
        totalItems,
        totalPrice,
        isInCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return ctx;
}
